import React, { useState, useCallback, memo, useRef, useContext, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Paper,
  CircularProgress,
  useMediaQuery,
  Divider,
  Card,
  CardContent,
  CardActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PreviewIcon from '@mui/icons-material/Preview';
import { postDocumento } from "../../componentes/http/actions/DocumentoAction";
import { postOrganismoEmisor } from "../../componentes/http/actions/OrganismoEmisorAction";
import { postEstadoTramitacion } from "../../componentes/http/actions/EstadoTramitacionAction";
import { postClasificacionDocumentos } from "../../componentes/http/actions/ClasificacionDocumentoAction";
import { postRubro } from "../../componentes/http/actions/RubroAction";
import { postClientes, getClientesAutomaticos } from "../../componentes/http/actions/ClientesAction";
import { postPais } from "../../componentes/http/actions/PaisAction";
import DataLoader from "../../componentes/data/DataLoader";
import CheckboxGroup from "../../componentes/data/CheckBox";
import { jwtDecode } from "jwt-decode";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import AutocompleteWithAddButton from "../../componentes/common/AutocompleteAddButton";
import MantenedorQuickAddModal from "../../componentes/common/MantenedorAddModal";
import { NotificacionContext } from "../../componentes/common/NotificacionContext";

const NuevaNormativa = () => {
  // Definimos un punto de quiebre para reorganizar el formulario
  const isSmallScreen = useMediaQuery('(max-width:1200px)');
  
  const { mostrarNotificacion } = useContext(NotificacionContext);

  // Referencia al DataLoader para poder recargar datos
  const dataLoaderRef = useRef(null);
  const navigate = useNavigate();
  const decoded = jwtDecode(localStorage.getItem("token"));
  const userId = decoded["oid"];
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    nombreAbreviado: "", 
    descripcion: "",
    descripcionAbreviado: "", 
    vigencia: "Desde su publicación", 
    codigo: "",
    url: "",
    enlaceResumen: "",
    creadoPor: "",
    organismoEmisorId: "",
    tipoDocumentoId: "",
    estadoTramitacionId: "",
    paisId: 1,
    fechaPublicacion: null,
    actualizacionNorma: false,
    rubroId: [],
    clasificacionDocumentoId: [],
    clienteId: [],
    compromisos: [],
    obligaciones: [],
  });
  const [fecha, setFecha] = useState(null);
  
  // Estados para las secciones de compromisos y obligaciones
  const [mostrarSeccionCompromisos, setMostrarSeccionCompromisos] = useState(false);
  const [mostrarSeccionObligaciones, setMostrarSeccionObligaciones] = useState(false);
  
  // Estados para los elementos actuales
  const [compromisoActual, setCompromisoActual] = useState("");
  const [obligacionActual, setObligacionActual] = useState("");

  const [loadedData, setLoadedData] = useState({
    organismos: [],
    tiposDocumento: [],
    estadosTramitacion: [],
    rubros: [],
    clasificaciones: [],
    clientes: [],
    paises: []
  });

  // NUEVO: Estado para tipos de documento filtrados
  const [tiposDocumentoFiltrados, setTiposDocumentoFiltrados] = useState([]);

  // Estados para controlar la apertura de los modales de añadir
  const [modalState, setModalState] = useState({
    organismoEmisor: false,
    estadoTramitacion: false,
    rubro: false,
    clasificacion: false,
    cliente: false,
    pais: false,
  });

  const [clientesAutomaticos, setClientesAutomaticos] = useState([]);
  
  const [validationErrors, setValidationErrors] = useState({
    rubros: false,
    clientes: false,
    clasificaciones: false,
    compromisos: false,
    obligaciones: false,
  });

  const [quickAddLoading, setQuickAddLoading] = useState(false);

  // NUEVO: Función para filtrar tipos de documento basado en organismo seleccionado
  const filtrarTiposDocumento = useCallback((organismoId) => {
    if (!organismoId || !loadedData.organismos || !loadedData.tiposDocumento) {
      setTiposDocumentoFiltrados(loadedData.tiposDocumento || []);
      return;
    }

    const organismoSeleccionado = loadedData.organismos.find(org => org.id === organismoId);
    
    if (organismoSeleccionado && organismoSeleccionado.idTipoDocumento && organismoSeleccionado.idTipoDocumento.length > 0) {
      // Filtrar tipos de documento que están asociados al organismo
      const tiposFiltrados = loadedData.tiposDocumento.filter(tipo => 
        organismoSeleccionado.idTipoDocumento.includes(tipo.id)
      );
      setTiposDocumentoFiltrados(tiposFiltrados);
    } else {
      // Si no hay tipos asociados, mostrar todos
      setTiposDocumentoFiltrados(loadedData.tiposDocumento || []);
    }
  }, [loadedData.organismos, loadedData.tiposDocumento]);

  // Función para truncar texto con límite de caracteres
  const truncarTexto = (texto, maxLength = 50) => {
    return texto.length > maxLength 
      ? texto.substring(0, maxLength) + "..." 
      : texto;
  };

  // Función para auto-generar campos abreviados
  const handleNombreChange = useCallback((event) => {
    const { value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      nombre: value,
      nombreAbreviado: value.substring(0, 400) // Auto-llenar con límite
    }));
  }, []);

  const handleDescripcionChange = useCallback((event) => {
    const { value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      descripcion: value,
      descripcionAbreviado: value.substring(0, 400) // Auto-llenar con límite
    }));
  }, []);

  const validateForm = () => {
    const errors = {
      rubros: formData.rubroId.length === 0,
      clientes: formData.clienteId.length === 0,
      clasificaciones: formData.clasificacionDocumentoId.length === 0,
      compromisos: mostrarSeccionCompromisos && formData.compromisos.length === 0,
      obligaciones: mostrarSeccionObligaciones && formData.obligaciones.length === 0
    };
    
    setValidationErrors(errors);
    
    // También validar otros campos obligatorios
     const hasBasicErrors = !formData.nombre || !fecha || !formData.tipoDocumentoId || 
    !formData.organismoEmisorId || !formData.estadoTramitacionId || !formData.url || !formData.descripcion;
  
    return !hasBasicErrors && !errors.rubros && !errors.clientes && !errors.compromisos && !errors.obligaciones;
  };

  // MODIFICADO: handleDataLoaded para inicializar tipos filtrados
  const handleDataLoaded = useCallback((data) => {
    setLoadedData(data);
    // Inicializar con todos los tipos de documento
    setTiposDocumentoFiltrados(data.tiposDocumento || []);
  }, []);

  const handleChange = useCallback((event) => {
    const { name, value, checked, type } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  // NUEVO: Función para manejar el cambio de organismo emisor
  const handleOrganismoChange = useCallback((event, newValue) => {
    const organismoId = newValue ? newValue.id : "";
    
    // Actualizar el organismo en el formData
    setFormData((prevData) => ({
      ...prevData,
      organismoEmisorId: organismoId,
      // Limpiar tipo de documento si el nuevo organismo no lo soporta
      tipoDocumentoId: organismoId ? prevData.tipoDocumentoId : "",
    }));

    // Filtrar tipos de documento
    filtrarTiposDocumento(organismoId);

    // Si había un tipo de documento seleccionado, verificar si sigue siendo válido
    if (formData.tipoDocumentoId && organismoId) {
      const organismoSeleccionado = loadedData.organismos.find(org => org.id === organismoId);
      if (organismoSeleccionado && 
          organismoSeleccionado.idTipoDocumento && 
          organismoSeleccionado.idTipoDocumento.length > 0 &&
          !organismoSeleccionado.idTipoDocumento.includes(formData.tipoDocumentoId)) {
        // Limpiar la selección de tipo de documento si ya no es válida
        setFormData((prevData) => ({
          ...prevData,
          tipoDocumentoId: "",
        }));
      }
    }
  }, [formData.tipoDocumentoId, loadedData.organismos, filtrarTiposDocumento]);

  const handleCheckboxChange = useCallback((event, id) => {
    const { name, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked
        ? [...prevData[name], id]
        : prevData[name].filter((item) => item !== id),
    }));
    
    // Limpiar error cuando se selecciona al menos un elemento (solo para rubros y clientes)
    if (checked) {
      if (name === 'rubroId') {
        setValidationErrors(prev => ({ ...prev, rubros: false }));
      } else if (name === 'clienteId') {
        setValidationErrors(prev => ({ ...prev, clientes: false }));
      }
    }
  }, []);
  
  // Funciones para abrir y cerrar los modales
  const handleOpenModal = (modalName) => {
    setModalState(prev => ({
      ...prev,
      [modalName]: true
    }));
  };
  
  const handleCloseModal = (modalName) => {
    setModalState(prev => ({
      ...prev,
      [modalName]: false
    }));
  };
  
  // MODIFICADO: Función para recargar datos con filtrado
  const reloadData = () => {
    if (dataLoaderRef.current && typeof dataLoaderRef.current.loadData === 'function') {
      dataLoaderRef.current.loadData();
      // Después de recargar, re-filtrar si hay un organismo seleccionado
      setTimeout(() => {
        if (formData.organismoEmisorId) {
          filtrarTiposDocumento(formData.organismoEmisorId);
        }
      }, 100);
    }
  };
  
  // Handlers para compromisos (simplificados)
  const agregarCompromiso = () => {
    if (!compromisoActual.trim()) {
      mostrarNotificacion("La descripción del compromiso es obligatoria", "error");
      return;
    }
    
    // Agregar el compromiso al formData
    setFormData(prevData => ({
      ...prevData,
      compromisos: [...prevData.compromisos, { descripcion: compromisoActual.trim() }]
    }));
    
    // Limpiar error de compromisos
    setValidationErrors(prev => ({ ...prev, compromisos: false }));
    
    // Resetear el compromiso actual
    setCompromisoActual("");
  };
  
  const eliminarCompromiso = (index) => {
    setFormData(prevData => {
      const nuevosCompromisos = [...prevData.compromisos];
      nuevosCompromisos.splice(index, 1);
      return {
        ...prevData,
        compromisos: nuevosCompromisos
      };
    });
  };

  // Handlers para obligaciones
  const agregarObligacion = () => {
    if (!obligacionActual.trim()) {
      mostrarNotificacion("La descripción de la obligación es obligatoria", "error");
      return;
    }
    
    // Agregar la obligación al formData
    setFormData(prevData => ({
      ...prevData,
      obligaciones: [...prevData.obligaciones, { descripcion: obligacionActual.trim() }]
    }));
    
    // Limpiar error de obligaciones
    setValidationErrors(prev => ({ ...prev, obligaciones: false }));
    
    // Resetear la obligación actual
    setObligacionActual("");
  };
  
  const eliminarObligacion = (index) => {
    setFormData(prevData => {
      const nuevasObligaciones = [...prevData.obligaciones];
      nuevasObligaciones.splice(index, 1);
      return {
        ...prevData,
        obligaciones: nuevasObligaciones
      };
    });
  };
  
  const obtenerClientesAutomaticos = useCallback(async () => {
    if (!formData.organismoEmisorId || !formData.tipoDocumentoId || formData.rubroId.length === 0) {
      setClientesAutomaticos([]);
      return;
    }

    try {
      const clienteIds = await getClientesAutomaticos(
        localStorage.getItem("token"),
        formData.organismoEmisorId,
        formData.tipoDocumentoId,
        formData.rubroId
      );

      const clientesCompletos = loadedData.clientes.filter(cliente => 
        clienteIds.includes(cliente.id)
      );

      setClientesAutomaticos(clientesCompletos);

      if (clientesCompletos.length > 0) {
        setFormData(prevData => ({
          ...prevData,
          clienteId: clienteIds
        }));
        
        setValidationErrors(prev => ({ ...prev, clientes: false }));
      
      }
    } catch (error) {
      console.error("Error al obtener clientes automáticos:", error);
      setClientesAutomaticos([]);
    }
  }, [formData.organismoEmisorId, formData.tipoDocumentoId, formData.rubroId, loadedData.clientes]);

  useEffect(() => {
    if (formData.organismoEmisorId && formData.tipoDocumentoId && formData.rubroId.length > 0) {
      const timeoutId = setTimeout(() => {
        obtenerClientesAutomaticos();
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setClientesAutomaticos([]);
    }
  }, [formData.organismoEmisorId, formData.rubroId.length, formData.tipoDocumentoId, obtenerClientesAutomaticos]);

  const handleAddOrganismoEmisor = async (data) => {
    try {
      setQuickAddLoading(true);
      await postOrganismoEmisor(localStorage.getItem("token"), data);
      
      reloadData();
      
      handleCloseModal('organismoEmisor');
      mostrarNotificacion(`Organismo emisor "${data.nombre}" agregado con éxito`, "success");
    } catch (error) {
      console.error("Error al agregar organismo emisor:", error);
      mostrarNotificacion("Error al agregar organismo emisor", "error");
    } finally {
      setQuickAddLoading(false);
    }
  };
  
  const handleAddEstadoTramitacion = async (data) => {
    try {
      setQuickAddLoading(true);
      await postEstadoTramitacion(localStorage.getItem("token"), data);
      
      // Recargar datos usando el DataLoader
      reloadData();
      
      handleCloseModal('estadoTramitacion');
      mostrarNotificacion(`Estado de tramitación "${data.nombre}" agregado con éxito`, "success");
    } catch (error) {
      console.error("Error al agregar estado de tramitación:", error);
      mostrarNotificacion("Error al agregar estado de tramitación", "error");
    } finally {
      setQuickAddLoading(false);
    }
  };
  
  const handleAddClasificacion = async (data) => {
    try {
      setQuickAddLoading(true);
      await postClasificacionDocumentos(localStorage.getItem("token"), data);
      
      // Recargar datos usando el DataLoader
      reloadData();
      
      handleCloseModal('clasificacion');
      mostrarNotificacion(`Clasificación "${data.nombre}" agregada con éxito`, "success");
    } catch (error) {
      console.error("Error al agregar clasificación:", error);
      mostrarNotificacion("Error al agregar clasificación", "error");
    } finally {
      setQuickAddLoading(false);
    }
  };
  
  const handleAddRubro = async (data) => {
    try {
      setQuickAddLoading(true);
      await postRubro(localStorage.getItem("token"), data);
      
      // Recargar datos usando el DataLoader
      reloadData();
      
      handleCloseModal('rubro');
      mostrarNotificacion(`Rubro "${data.nombre}" agregado con éxito`, "success");
    } catch (error) {
      console.error("Error al agregar rubro:", error);
      mostrarNotificacion("Error al agregar rubro", "error");
    } finally {
      setQuickAddLoading(false);
    }
  };
  
  const handleAddCliente = async (data) => {
    try {
      setQuickAddLoading(true);
      await postClientes(localStorage.getItem("token"), data);
      
      // Recargar datos usando el DataLoader
      reloadData();
      
      handleCloseModal('cliente');
      mostrarNotificacion (`Cliente "${data.nombre}" agregado con éxito`, "success");
    } catch (error) {
      console.error("Error al agregar cliente:", error);
      mostrarNotificacion("Error al agregar cliente", "error");
    } finally {
      setQuickAddLoading(false);
    }
  };

  const handleAddPais = async (data) => {
    try {
      setQuickAddLoading(true);
      await postPais(localStorage.getItem("token"), data);

      // Recargar datos usando el DataLoader
      reloadData();
      handleCloseModal('pais');
      mostrarNotificacion (`País "${data.nombre}" agregado con éxito`, "success");
    } catch (error) {
      console.error("Error al agregar país:", error);
      mostrarNotificacion("Error al agregar país", "error");
    } finally {
      setQuickAddLoading(false);
    }
  };

  const handleMostrarCompromisosChange = (e) => {
    const isChecked = e.target.checked;
    setMostrarSeccionCompromisos(isChecked);
    
    // Si se desmarca, limpiar el error y los compromisos
    if (!isChecked) {
      setValidationErrors(prev => ({ ...prev, compromisos: false }));
      setFormData(prevData => ({
        ...prevData,
        compromisos: []
      }));
      setCompromisoActual("");
    }
  };

  const handleMostrarObligacionesChange = (e) => {
    const isChecked = e.target.checked;
    setMostrarSeccionObligaciones(isChecked);
    
    // Si se desmarca, limpiar el error y las obligaciones
    if (!isChecked) {
      setValidationErrors(prev => ({ ...prev, obligaciones: false }));
      setFormData(prevData => ({
        ...prevData,
        obligaciones: []
      }));
      setObligacionActual("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      mostrarNotificacion("Por favor, completa todos los campos obligatorios", "error");
      return;
    }
    
    setIsLoading(true);
    try {
      const dataToSubmit = {
        ...formData,
        creadoPor: userId,
        fechaPublicacion: fecha ? fecha.toISOString() : null,
        actualizacionNorma: formData.actualizacionNorma,
        compromisos: mostrarSeccionCompromisos ? formData.compromisos : [],
        obligaciones: mostrarSeccionObligaciones ? formData.obligaciones : []
      };

      const response = await postDocumento(localStorage.getItem("token"), dataToSubmit);
      const nombreTruncado = truncarTexto(formData.nombre, 60);
      const mensajeExito = `La normativa "ID: ${response} - Nombre: ${nombreTruncado}" ha sido creada con éxito`;
      
      
      mostrarNotificacion(mensajeExito, "success");
      
      // Redirigir después de 1 segundos
      navigate('/gestor-normativa')
      setFormData({
        nombre: "",
        nombreAbreviado: "",
        descripcion: "",
        descripcionAbreviado: "",
        vigencia: "Desde su publicación",
        codigo: "",
        url: "",
        enlaceResumen: "",
        creadoPor: "",
        fechaPublicacion: null,
        organismoEmisorId: "",
        tipoDocumentoId: "",
        estadoTramitacionId: "",
        paisId: 1,
        rubroId: [],
        clasificacionDocumentoId: [],
        clienteId: [],
        actualizacionNorma: false,
        compromisos: [],
        obligaciones: [],
      });
      setFecha(null);
      setCompromisoActual("");
      setObligacionActual("");
      setMostrarSeccionCompromisos(false);
      setMostrarSeccionObligaciones(false);
      setTiposDocumentoFiltrados(loadedData.tiposDocumento || []);
    } catch (error) {
      console.error("Error al agregar normativa:", error);
      mostrarNotificacion("Error al agregar normativa", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Definir los campos para cada tipo de mantenedor
  
  const organismoEmisorFields = [
    { name: 'nombre', label: 'Nombre', required: true },
    { name: 'sigla', label: 'Sigla', required: true },
    { name: 'nombreAbreviado', label: 'Nombre Abreviado', required: false },
  ];
  
  const estadoTramitacionFields = [
    { name: 'nombre', label: 'Nombre', required: true },
  ];
  
  const clasificacionFields = [
    { name: 'nombre', label: 'Nombre', required: true },
  ];
  
  const rubroFields = [
    { name: 'nombre', label: 'Nombre', required: true },
  ];
  
  const clienteFields = [
    { name: 'nombre', label: 'Nombre', required: true },
    { name: 'tipoCorreo', label: 'Tipo de Correo', required: true },
  ];

  const paisFields = [
    { name: 'nombre', label: 'Nombre', required: true },
    { name: 'codigoISO', label: 'Código ISO', required: true },
  ];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{paddingTop: {xs: '20px', sm: '10px'}}}>
      <DataLoader 
        onDataLoaded={handleDataLoaded} 
        ref={dataLoaderRef}
      />
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Agregar Normativa
      </Typography>

      {/* Formulario principal de la normativa */}
        <Grid container spacing={2} elevation={3} >
          {/* En pantallas pequeñas, cada elemento tomará 12 columnas (ancho completo) */}
          <Grid item xs={12} md={isSmallScreen ? 12 : 8}>
            <Grid container spacing={2}>
              {/* Modificamos cada elemento del Grid para ocupar ancho completo en pantallas pequeñas */}
              <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
                <TextField
                  fullWidth
                  label="Nombre"
                  name="nombre"
                  value={formData.nombre}
                  inputProps={{ maxLength: 100}}
                   helperText={`${formData.nombre.length}/100 caracteres`}
                  onChange={handleNombreChange}
                  required={true}
                />
              </Grid>
              <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    sx={{ width: "100%" }}
                    format="dd/MM/yyyy"
                    label="Fecha de publicación"
                    value={fecha}
                    onChange={(date) => setFecha(date)}
                    slotProps={{
                      textField: {
                        required: true,
                        fullWidth: true
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
                <AutocompleteWithAddButton
                  options={loadedData.organismos}
                  label="Organismo emisor"
                  required={true}
                  value={
                    loadedData.organismos.find(
                      (org) => org.id === formData.organismoEmisorId
                    ) || null
                  }
                  sx={{ mt: -1 }}
                  onChange={handleOrganismoChange}
                  onAddNewClick={() => handleOpenModal('organismoEmisor')}
                />
              </Grid>
              <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
                <Autocomplete
                  fullWidth
                  options={tiposDocumentoFiltrados}
                  getOptionLabel={(option) => option.nombre || ''}
                  value={
                    tiposDocumentoFiltrados.find(
                      (tipo) => tipo.id === formData.tipoDocumentoId
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleChange({
                      target: {
                        name: "tipoDocumentoId",
                        value: newValue ? newValue.id : "",
                      },
                    });
                  }}
                  disabled={!formData.organismoEmisorId}
                  sx={{ mt: 1, mb: 1 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tipo de Documento"
                      required={true}
                      helperText={!formData.organismoEmisorId ? "Primero selecciona un organismo emisor" : ""}
                                         
                    />
                  )}
                  noOptionsText="No hay tipos de documento disponibles"
                />
              </Grid>
              
              <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
                <AutocompleteWithAddButton
                  options={loadedData.estadosTramitacion}
                  label="Estado de tramitación"
                  required={true}
                  value={
                    loadedData.estadosTramitacion.find(
                      (estado) => estado.id === formData.estadoTramitacionId
                    ) || null
                  }
                  sx={{ mt: -2 }}
                  onChange={(event, newValue) => {
                    handleChange({
                      target: {
                        name: "estadoTramitacionId",
                        value: newValue ? newValue.id : "",
                      },
                    });
                  }}
                  onAddNewClick={() => handleOpenModal('estadoTramitacion')}
                />
              </Grid>
              <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
                <TextField
                  fullWidth
                  type="url"
                  label="URL"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  required={true}
                />
              </Grid>
              
              <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
                <TextField
                  fullWidth
                  multiline
                  rows={isSmallScreen ? 8 : 6}
                  label="Descripción"
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleDescripcionChange}
                  required={true}
                />
              </Grid>
              <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
                <TextField
                  fullWidth
                  label="Enlace resumen"
                  name="enlaceResumen"
                  value={formData.enlaceResumen}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
                <AutocompleteWithAddButton
                  options={loadedData.paises}
                  label="País"
                  required={true}
                  value={
                    loadedData.paises.find(
                      (pais) => pais.id === formData.paisId
                    ) || null
                  }
                  sx={{ mt: -1 }}
                  onChange={(event, newValue) => {
                    handleChange({
                      target: {
                        name: "paisId",
                        value: newValue ? newValue.id : "",
                      },
                    });
                  }}
                  onAddNewClick={() => handleOpenModal('pais')}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.actualizacionNorma}
                      onChange={(e) => handleChange(e)}
                      name="actualizacionNorma"
                      type="checkbox"
                    />
                  }
                  label="Es actualización de normativa"
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Sección "Aplica a" con botón para agregar nueva clasificación */}
          <Grid item xs={12} md={isSmallScreen ? 12 : 4}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: { xs: 2, sm: 3 }, 
                mb: 3, 
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">Aplica a</Typography>
              <Button 
                size="small" 
                color="primary" 
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenModal('clasificacion')}
              >
                Añadir
              </Button>
            </Box>
            <CheckboxGroup
              title=""
              items={loadedData.clasificaciones}
              selectedItems={formData.clasificacionDocumentoId}
              onChange={handleCheckboxChange}
              name="clasificacionDocumentoId"
            />
            </Paper>
          </Grid>
          
        </Grid>

      {/* Sección Rubros y Clientes con botones para agregar nuevos */}
        <Grid container spacing={2} elevation={3}>
          
          <Grid item xs={12} md={isSmallScreen ? 12 : 6} lg={isSmallScreen ? 12 : 4}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: { xs: 2, sm: 3 }, 
                mb: 3, 
                borderRadius: 3,
                border: '1px solid',
                borderColor: validationErrors.rubros ? 'error.main' : 'divider'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" color={validationErrors.rubros ? "error" : "inherit"}>
                  Rubros
                  <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
                </Typography>
                <Button 
                  size="small"
                  color="primary" 
                 variant="contained"
                 startIcon={<AddIcon />}
                 onClick={() => handleOpenModal('rubro')}
               >
                 Añadir
               </Button>
             </Box>
             <CheckboxGroup
               title=""
               items={loadedData.rubros}
               selectedItems={formData.rubroId}
               onChange={handleCheckboxChange}
               name="rubroId"
               showSelectAll={true}
               required={true}
               error={validationErrors.rubros}
               helperText={validationErrors.rubros ? "Debe seleccionar al menos un rubro" : ""}
             />
           </Paper>
         </Grid>
         <Grid item xs={12} md={isSmallScreen ? 12 : 6} lg={isSmallScreen ? 12 : 4}>
           <Paper 
             elevation={3} 
             sx={{ 
               p: { xs: 2, sm: 3 }, 
               mb: 3, 
               borderRadius: 3,
               border: '1px solid',
               borderColor: validationErrors.clientes ? 'error.main' : 'divider'
             }}
           >
             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
               <Typography variant="h6" color={validationErrors.clientes ? "error" : "inherit"}>
                  Clientes
                  <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
                  {clientesAutomaticos.length > 0 && (
                    <Typography component="span" sx={{ ml: 1, fontSize: '0.875rem', fontWeight: 'normal', color: 'success.main' }}>
                      ({clientesAutomaticos.length} preseleccionados)
                    </Typography>
                  )}
                </Typography>
               <Button 
                 size="small" 
                 color="primary" 
                 variant="contained"
                 startIcon={<AddIcon />}
                 onClick={() => handleOpenModal('cliente')}
               >
                 Añadir
               </Button>
             </Box>
             <CheckboxGroup
               title=""
               items={loadedData.clientes}
               selectedItems={formData.clienteId}
               onChange={handleCheckboxChange}
               name="clienteId"
               showSelectAll={true}
               required={true}
               error={validationErrors.clientes}
               helperText={validationErrors.clientes ? "Debe seleccionar al menos un cliente" : ""}
               layoutMode="grid"
               columns={2}
             />
           </Paper>
         </Grid>
       </Grid>
       <Accordion defaultExpanded>
         <AccordionSummary
           expandIcon={<ExpandMoreIcon />}
           aria-controls="newsletter-content"
           id="newsletter-header"
         >
           <Box sx={{ display: 'flex', alignItems: 'center' }}>
             <PreviewIcon sx={{ mr: 1, color: 'primary.main' }} />
             <Typography variant="h6">
               Datos Newsletter
             </Typography>
           </Box>
         </AccordionSummary>
         <AccordionDetails>
           <Grid container spacing={2}>
             <Grid item xs={12}>
               <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                 Personaliza cómo se verá esta normativa en el newsletter
               </Typography>
             </Grid>
             
             <Grid item xs={12} sm={6}>
               <TextField
                 fullWidth
                 label="Nombre Abreviado (Newsletter)"
                 name="nombreAbreviado"
                 value={formData.nombreAbreviado}
                 onChange={handleChange}
                 inputProps={{ maxLength: 100}}
                 helperText={`${formData.nombreAbreviado.length}/100 caracteres`}
               />
             </Grid>
             
             <Grid item xs={12} sm={6}>
               <TextField
                 fullWidth
                 label="Vigencia"
                 name="vigencia"
                 value={formData.vigencia}
                 onChange={handleChange}
               />
             </Grid>
             
             <Grid item xs={12}>
               <TextField
                 fullWidth
                 multiline
                 rows={4}
                 label="Descripción Abreviada (Newsletter)"
                 name="descripcionAbreviado"
                 value={formData.descripcionAbreviado}
                 onChange={handleChange}
                 inputProps={{ maxLength: 400 }}
                 helperText={`${formData.descripcionAbreviado.length}/400 caracteres`}
               />
             </Grid>          
           </Grid>
         </AccordionDetails>
       </Accordion>

     {/* SECCIÓN: Compromisos y Obligaciones */}
     <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
       <Grid container spacing={2}>
         <Grid item xs={12}>
           <Typography variant="h6" gutterBottom>
             Compromisos y Obligaciones
           </Typography>
         </Grid>
         
         <Grid item xs={12} sm={6}>
           <FormControlLabel
             control={
               <Checkbox
                 checked={mostrarSeccionCompromisos}
                 onChange={handleMostrarCompromisosChange}
                 name="mostrarSeccionCompromisos"
               />
             }
             label={
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 ¿Deseas añadir compromisos a esta normativa?
                 {mostrarSeccionCompromisos && (
                   <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
                 )}
               </Box>
             }
           />
         </Grid>
         
         <Grid item xs={12} sm={6}>
           <FormControlLabel
             control={
               <Checkbox
                 checked={mostrarSeccionObligaciones}
                 onChange={handleMostrarObligacionesChange}
                 name="mostrarSeccionObligaciones"
               />
             }
             label={
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 ¿Deseas añadir obligaciones a esta normativa?
                 {mostrarSeccionObligaciones && (
                   <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
                 )}
               </Box>
             }
           />
         </Grid>
         
         {/* Sección de Compromisos */}
         {mostrarSeccionCompromisos && (
           <>
             <Grid item xs={12}>
               <Divider sx={{ my: 2 }} />
               <Typography 
                 variant="h6" 
                 gutterBottom
                 color={validationErrors.compromisos ? "error" : "inherit"}
               >
                 Agregar Compromisos
                 <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
               </Typography>
               {validationErrors.compromisos && (
                 <Typography variant="caption" color="error" display="block" sx={{ mt: -1, mb: 1 }}>
                   Debe agregar al menos un compromiso
                 </Typography>
               )}
             </Grid>
             
             <Grid item xs={12} sm={8}>
               <TextField
                 fullWidth
                 multiline
                 rows={3}
                 label="Descripción del compromiso"
                 value={compromisoActual}
                 onChange={(e) => setCompromisoActual(e.target.value)}
                 error={validationErrors.compromisos}
               />
             </Grid>
             
             <Grid item xs={12} sm={4}>
               <Button
                 variant="contained"
                 color="primary"
                 onClick={agregarCompromiso}
                 startIcon={<AddIcon />}
                 fullWidth
                 sx={{ height: '56px' }}
               >
                 Añadir compromiso
               </Button>
             </Grid>
             
             {/* Lista de compromisos agregados */}
             {formData.compromisos.length > 0 && (
               <Grid item xs={12}>
                 <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                   Compromisos agregados ({formData.compromisos.length})
                 </Typography>
                 
                 {formData.compromisos.map((compromiso, index) => (
                   <Card key={index} variant="outlined" sx={{ mb: 1 }}>
                     <CardContent sx={{ pb: 1 }}>
                       <Typography variant="body2">
                         {compromiso.descripcion}
                       </Typography>
                     </CardContent>
                     <CardActions sx={{ pt: 0 }}>
                       <Button 
                         size="small" 
                         color="error" 
                         startIcon={<DeleteIcon />}
                         onClick={() => eliminarCompromiso(index)}
                       >
                         Eliminar
                       </Button>
                     </CardActions>
                   </Card>
                 ))}
               </Grid>
             )}
           </>
         )}
         
         {/* Sección de Obligaciones */}
         {mostrarSeccionObligaciones && (
           <>
             <Grid item xs={12}>
               <Divider sx={{ my: 2 }} />
               <Typography 
                 variant="h6" 
                 gutterBottom
                 color={validationErrors.obligaciones ? "error" : "inherit"}
               >
                 Agregar Obligaciones
                 <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
               </Typography>
               {validationErrors.obligaciones && (
                 <Typography variant="caption" color="error" display="block" sx={{ mt: -1, mb: 1 }}>
                   Debe agregar al menos una obligación
                 </Typography>
               )}
             </Grid>
             
             <Grid item xs={12} sm={8}>
               <TextField
                 fullWidth
                 multiline
                 rows={3}
                 label="Descripción de la obligación"
                 value={obligacionActual}
                 onChange={(e) => setObligacionActual(e.target.value)}
                 error={validationErrors.obligaciones}
               />
             </Grid>
             
             <Grid item xs={12} sm={4}>
               <Button
                 variant="contained"
                 color="secondary"
                 onClick={agregarObligacion}
                 startIcon={<AddIcon />}
                 fullWidth
                 sx={{ height: '56px' }}
               >
                 Añadir obligación
               </Button>
             </Grid>
             
             {/* Lista de obligaciones agregadas */}
             {formData.obligaciones.length > 0 && (
               <Grid item xs={12}>
                 <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                   Obligaciones agregadas ({formData.obligaciones.length})
                 </Typography>
                 
                 {formData.obligaciones.map((obligacion, index) => (
                   <Card key={index} variant="outlined" sx={{ mb: 1 }}>
                    <CardContent sx={{ pb: 1 }}>
                      <Typography variant="body2">
                        {obligacion.descripcion}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ pt: 0 }}>
                      <Button 
                        size="small" 
                        color="error" 
                        startIcon={<DeleteIcon />}
                        onClick={() => eliminarObligacion(index)}
                      >
                        Eliminar
                      </Button>
                    </CardActions>
                  </Card>
                ))}
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Paper>

    <Button 
      type="submit" 
      fullWidth 
      variant="contained" 
      sx={{ mt: 3, mb: 2 }} 
      disabled={isLoading}
    >
      {isLoading ? (
        <>
          <CircularProgress size={24} sx={{ mr: 1, color: 'white' }} />
          Procesando...
        </>
      ) : (
        "Agregar Normativa"
      )}
    </Button>
    
    {/* Modales para agregar nuevos elementos */}
    
    <MantenedorQuickAddModal
      open={modalState.organismoEmisor}
      onClose={() => handleCloseModal('organismoEmisor')}
      onSubmit={handleAddOrganismoEmisor}
      title="Nuevo Organismo Emisor"
      fields={organismoEmisorFields}
      isLoading={quickAddLoading}
    />
    
    <MantenedorQuickAddModal
      open={modalState.estadoTramitacion}
      onClose={() => handleCloseModal('estadoTramitacion')}
      onSubmit={handleAddEstadoTramitacion}
      title="Nuevo Estado de Tramitación"
      fields={estadoTramitacionFields}
      isLoading={quickAddLoading}
    />
    
    <MantenedorQuickAddModal
      open={modalState.clasificacion}
      onClose={() => handleCloseModal('clasificacion')}
      onSubmit={handleAddClasificacion}
      title="Nueva Clasificación"
      fields={clasificacionFields}
      isLoading={quickAddLoading}
    />
    
    <MantenedorQuickAddModal
      open={modalState.rubro}
      onClose={() => handleCloseModal('rubro')}
      onSubmit={handleAddRubro}
      title="Nuevo Rubro"
      fields={rubroFields}
      isLoading={quickAddLoading}
    />
    
    <MantenedorQuickAddModal
      open={modalState.cliente}
      onClose={() => handleCloseModal('cliente')}
      onSubmit={handleAddCliente}
      title="Nuevo Cliente"
      fields={clienteFields}
      isLoading={quickAddLoading}
    />

    <MantenedorQuickAddModal
      open={modalState.pais}
      onClose={() => handleCloseModal('pais')}
      onSubmit={handleAddPais}
      title="Nuevo País"
      fields={paisFields}
      isLoading={quickAddLoading}
    />
    
  </Box>
);
};

export default memo(NuevaNormativa);